/**
 * DocumentLifecycleManager - Complete document lifecycle management for ClientFirst
 * 
 * Orchestrates document creation, loading, saving, and versioning workflows
 * while integrating with DMS backend and maintaining ClientFirst architecture.
 */

import { DMSDocumentService, DocumentCreateData, DocumentLoadResult, SaveResult } from './DMSDocumentService';
import { AutoSaveManager } from './AutoSaveManager';
import { DocumentEngineContract } from '../interfaces/DocumentEngineContract';
import { LocalDocumentState, AttorneyInfo } from '../types/DocumentTypes';

export interface DocumentCreationOptions {
  title: string;
  client_id?: string;
  matter_id?: string;
  document_type?: string;
  initialContent?: string;
  attorney: AttorneyInfo;
}

export interface DocumentSaveOptions {
  createNewVersion?: boolean;
  changeSummary?: string;
  forceImmediate?: boolean;
  metadata?: any; // Page setup and other document metadata
}

export interface DocumentLoadOptions {
  loadWorkingCopy?: boolean;
  initializeAutoSave?: boolean;
}

export interface LifecycleResult {
  success: boolean;
  message: string;
  documentId?: string;
  versionId?: string;
  data?: any;
  errors?: string[];
}

export interface DocumentMetrics {
  documentId: string;
  title: string;
  createdAt: Date;
  lastSavedAt: Date | null;
  totalSaves: number;
  autoSaves: number;
  manualSaves: number;
  versions: number;
  currentTextLength: number;
  hasUnsavedChanges: boolean;
}

export class DocumentLifecycleManager {
  private dmsService: DMSDocumentService;
  private autoSaveManager: AutoSaveManager | null;
  private documentEngine: DocumentEngineContract | null = null;
  
  // Current document state
  private currentDocumentId: string | null = null;
  private currentVersionId: string | null = null;
  private currentTitle: string | null = null;
  private createdAt: Date | null = null;
  private lastSavedAt: Date | null = null;
  private totalSaves: number = 0;
  private autoSaves: number = 0;
  private manualSaves: number = 0;
  private versions: number = 0;

  constructor(dmsService: DMSDocumentService, autoSaveManager: AutoSaveManager | null) {
    this.dmsService = dmsService;
    this.autoSaveManager = autoSaveManager;
    
    // Listen to auto-save events (only if autoSaveManager exists)
    if (this.autoSaveManager) {
      this.autoSaveManager.on('save-completed', this.handleAutoSaveCompleted.bind(this));
    }
    
    console.log('[DocumentLifecycleManager] Initialized', autoSaveManager ? 'with AutoSave' : 'manual save only');
  }

  /**
   * Create a new document
   */
  async createNewDocument(options: DocumentCreationOptions): Promise<LifecycleResult> {
    try {
      console.log('[DocumentLifecycleManager] Creating new document:', options.title);

      // Validate required fields
      if (!options.title || !options.attorney) {
        return {
          success: false,
          message: 'Title and attorney information are required',
          errors: ['Missing required fields']
        };
      }

      // Prepare document data
      const documentData: DocumentCreateData = {
        title: options.title,
        content: options.initialContent || '', // Start with empty or provided content
        client_id: options.client_id,
        matter_id: options.matter_id,
        document_type: options.document_type || 'document'
      };

      // Create document in DMS
      const result = await this.dmsService.createDocument(documentData);
      
      // Initialize document state
      this.currentDocumentId = result.document_id;
      this.currentTitle = options.title;
      this.createdAt = new Date();
      this.lastSavedAt = new Date();
      this.totalSaves = 1; // Creation counts as first save
      this.versions = 1;
      
      console.log('[DocumentLifecycleManager] Document created successfully:', {
        documentId: result.document_id,
        versionId: result.version_id,
        title: options.title
      });

      return {
        success: true,
        message: 'Document created successfully',
        documentId: result.document_id,
        versionId: result.version_id,
        data: {
          documentId: result.document_id,
          versionId: result.version_id,
          versionNumber: result.version_number,
          title: options.title,
          initialContent: documentData.content
        }
      };

    } catch (error) {
      console.error('[DocumentLifecycleManager] Document creation failed:', error);
      return {
        success: false,
        message: `Failed to create document: ${error instanceof Error ? error.message : String(error)}`,
        errors: [error instanceof Error ? error.message : String(error)]
      };
    }
  }

  /**
   * Load an existing document
   */
  async loadExistingDocument(
    documentId: string, 
    documentEngine: DocumentEngineContract,
    options: DocumentLoadOptions & { versionId?: string } = {}
  ): Promise<LifecycleResult> {
    try {
      console.log('[DocumentLifecycleManager] Loading document:', documentId);

      // Load document from DMS with page setup metadata
      const loadResult: DocumentLoadResult = await this.dmsService.loadDocumentForClientFirst(documentId);
      
      // Determine content to load
      let contentToLoad = loadResult.textContent;
      if (options.loadWorkingCopy && loadResult.workingCopy) {
        contentToLoad = loadResult.workingCopy;
        console.log('[DocumentLifecycleManager] Loading working copy instead of latest version');
      }

      // Initialize document engine with loaded content
      const initialState: LocalDocumentState = {
        documentId: documentId,
        text: contentToLoad,
        caretPosition: 0,
        selection: null,
        version: loadResult.latestVersion?.version_number || 1,
        isDirty: false,
        created: new Date(loadResult.document.created_at),
        modified: new Date(loadResult.document.updated_at),
        attorney: loadResult.document.attorney || { 
          id: 'unknown', 
          name: 'Unknown', 
          email: 'unknown@example.com',
          barNumber: '',
          firmName: '',
          role: 'attorney',
          seniority: 0
        },
        documentType: loadResult.document.document_type || 'document',
        jurisdiction: loadResult.document.jurisdiction || 'federal',
        lastServerSync: Date.now(),
        serverVersion: loadResult.latestVersion?.version_number || 1,
        dirtyRegions: new Set(),
        title: loadResult.document.title,
        matterNumber: loadResult.document.matter_number,
        clientName: loadResult.document.client_name
      };

      await documentEngine.initialize(initialState);
      
      // Set up lifecycle tracking
      this.documentEngine = documentEngine;
      this.currentDocumentId = documentId;
      this.currentVersionId = options.versionId || loadResult.document.latest_version_id;
      this.currentTitle = loadResult.document.title;
      this.createdAt = new Date(loadResult.document.created_at);
      this.lastSavedAt = loadResult.document.updated_at ? new Date(loadResult.document.updated_at) : null;
      this.versions = loadResult.document.versions?.length || 1;
      this.totalSaves = 0; // Reset counters for loaded document
      this.autoSaves = 0;
      this.manualSaves = 0;

      // Start auto-save if requested
      if (options.initializeAutoSave !== false) { // Default to true
        // AutoSave removed - manual save only
        // this.autoSaveManager.startAutoSave(documentId, documentEngine);
        console.log('[DocumentLifecycleManager] Auto-save initialized for loaded document');
      }

      console.log('[DocumentLifecycleManager] Document loaded successfully:', {
        documentId,
        title: loadResult.document.title,
        contentLength: contentToLoad.length,
        versionsCount: this.versions,
        hasWorkingCopy: !!loadResult.workingCopy
      });

      return {
        success: true,
        message: 'Document loaded successfully',
        documentId,
        data: {
          document: loadResult.document,
          textContent: contentToLoad,
          latestVersion: loadResult.latestVersion,
          hasWorkingCopy: !!loadResult.workingCopy,
          initialState,
          pageSetupMetadata: loadResult.pageSetupMetadata
        }
      };

    } catch (error) {
      console.error('[DocumentLifecycleManager] Document loading failed:', error);
      return {
        success: false,
        message: `Failed to load document: ${error instanceof Error ? error.message : String(error)}`,
        errors: [error instanceof Error ? error.message : String(error)]
      };
    }
  }

  /**
   * Save document (working copy or new version)
   */
  async saveDocument(documentId: string, options: DocumentSaveOptions = {}): Promise<LifecycleResult> {
    try {
      if (!this.documentEngine) {
        return {
          success: false,
          message: 'No document engine available',
          errors: ['Document engine not initialized']
        };
      }

      console.log('[DocumentLifecycleManager] Saving document:', {
        documentId,
        createNewVersion: options.createNewVersion,
        changeSummary: options.changeSummary
      });

      let result: SaveResult | { document_id: string; version_id: string; version_number: number };
      let saveType: string;

      // Get current content from document engine
      const content = (this.documentEngine as any).getText?.() || '';

      if (options.createNewVersion) {
        // Create new version
        // Create version using DMS service directly (AutoSave removed)
        const versionResult = await this.dmsService.createNewVersion(documentId, {
          content: content,
          change_summary: options.changeSummary || 'New version created'
        });
        const versionId = versionResult.version_id;
        
        result = {
          success: true,
          message: 'New version created successfully',
          versionId
        };
        
        saveType = 'version';
        this.versions++;
        this.manualSaves++;
      } else {
        // Save working copy
        if (options.forceImmediate) {
          // Force save using DMS service directly (AutoSave removed)
          const saveMetadata = { 
            versionId: this.currentVersionId,
            ...options.metadata // Include page setup metadata
          };
          result = await this.dmsService.updateDocumentContent(documentId, content, saveMetadata);
        } else {
          // Mark for auto-save
          // Mark dirty removed (AutoSave removed)
          // this.autoSaveManager.markDirty();
          result = {
            success: true,
            message: 'Changes marked for auto-save'
          };
        }
        
        saveType = 'working-copy';
      }

      this.totalSaves++;
      this.lastSavedAt = new Date();

      console.log('[DocumentLifecycleManager] Document saved successfully:', {
        documentId,
        saveType,
        success: result.success
      });

      return {
        success: result.success,
        message: result.message,
        documentId,
        versionId: result.versionId,
        data: {
          saveType,
          timestamp: this.lastSavedAt,
          totalSaves: this.totalSaves
        }
      };

    } catch (error) {
      console.error('[DocumentLifecycleManager] Document save failed:', error);
      return {
        success: false,
        message: `Failed to save document: ${error instanceof Error ? error.message : String(error)}`,
        errors: [error instanceof Error ? error.message : String(error)]
      };
    }
  }

  /**
   * Initialize document engine and auto-save for a document
   */
  async initializeDocument(
    documentId: string,
    documentEngine: DocumentEngineContract,
    options: DocumentLoadOptions = {}
  ): Promise<LifecycleResult> {
    // This is a convenience method that combines loading and initialization
    return await this.loadExistingDocument(documentId, documentEngine, options);
  }

  /**
   * Clean up resources when document is closed
   */
  cleanup(): void {
    console.log('[DocumentLifecycleManager] Cleaning up document lifecycle');
    
    // Stop auto-save
    // AutoSave removed
    // this.autoSaveManager.stopAutoSave();
    
    // Reset state
    this.documentEngine = null;
    this.currentDocumentId = null;
    this.currentVersionId = null;
    this.currentTitle = null;
    this.createdAt = null;
    this.lastSavedAt = null;
    this.totalSaves = 0;
    this.autoSaves = 0;
    this.manualSaves = 0;
    this.versions = 0;
  }

  /**
   * Get current document metrics
   */
  getDocumentMetrics(): DocumentMetrics | null {
    if (!this.currentDocumentId || !this.documentEngine) {
      return null;
    }

    const documentState = this.documentEngine.getState();
    
    return {
      documentId: this.currentDocumentId,
      title: this.currentTitle || 'Untitled',
      createdAt: this.createdAt || new Date(),
      lastSavedAt: this.lastSavedAt,
      totalSaves: this.totalSaves,
      autoSaves: this.autoSaves,
      manualSaves: this.manualSaves,
      versions: this.versions,
      currentTextLength: documentState.text.length,
      hasUnsavedChanges: documentState.isDirty // AutoSave removed - only check document state
    };
  }

  /**
   * Get lifecycle status
   */
  getStatus(): {
    hasActiveDocument: boolean;
    documentId: string | null;
    title: string | null;
    metrics: DocumentMetrics | null;
  } {
    return {
      hasActiveDocument: !!this.currentDocumentId,
      documentId: this.currentDocumentId,
      title: this.currentTitle,
      // AutoSave removed
      // autoSaveStatus: this.autoSaveManager.getStatus(),
      metrics: this.getDocumentMetrics()
    };
  }

  /**
   * Force immediate save of current document
   */
  async forceSave(): Promise<LifecycleResult> {
    if (!this.currentDocumentId) {
      return {
        success: false,
        message: 'No active document to save'
      };
    }

    return await this.saveDocument(this.currentDocumentId, { forceImmediate: true });
  }

  /**
   * Create new version of current document
   */
  async createNewVersion(changeSummary?: string): Promise<LifecycleResult> {
    if (!this.currentDocumentId) {
      return {
        success: false,
        message: 'No active document to version'
      };
    }

    return await this.saveDocument(this.currentDocumentId, { 
      createNewVersion: true, 
      changeSummary 
    });
  }

  // Private event handlers

  private handleAutoSaveCompleted(event: any): void {
    if (event.type === 'working-copy') {
      this.autoSaves++;
      this.totalSaves++;
      this.lastSavedAt = new Date();
      
      console.log('[DocumentLifecycleManager] Auto-save completed:', {
        documentId: event.documentId,
        totalAutoSaves: this.autoSaves
      });
    }
  }
}
